import { Component, effect, inject, OnDestroy, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  PriceTemplateClass,
  PriceTemplateRangeClass,
  PriceTemplateRulesClass,
} from './class/price-template-class';
import { InputSwitchModule } from 'primeng/inputswitch';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule, NgForm } from '@angular/forms';
import { DividerModule } from 'primeng/divider';
import {
  CurrencyEnum,
  CurrencyPipe,
  EventThrowingEnum,
  HovergifDirective,
  PercentageTypeEnum,
  priceSourceAll,
  providerProductIncrementTypeConst,
  ProviderProductIncrementTypeEnum,
} from '@component-library';
import { InputNumberModule } from 'primeng/inputnumber';
import { StoreService } from '../../../core/store/store.service';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import {
  DialogService,
  DynamicDialogComponent,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import {
  PriceTemplateInterface,
  PriceTemplateRangeInterface,
  PriceTemplateRuleInterface,
} from '../../../core/interfaces/price-template/price-template';
import { TuiMoneyModule } from '@taiga-ui/addon-commerce';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { PriceTemplateService } from '../../../core/services/price-template/price-template.service';
import { GlobalConfirmationService } from '../../../core/services/global-services/global-confirmation-service/global-confirmation.service';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { Select } from 'primeng/select';
import { ToggleSwitch } from 'primeng/toggleswitch';

@Component({
  selector: 'magenty-add-price-template',
  imports: [
    CommonModule,
    InputSwitchModule,
    InputTextModule,
    FormsModule,
    DividerModule,
    CurrencyPipe,
    InputNumberModule,
    TableModule,
    ButtonModule,
    TuiMoneyModule,
    DialogModule,
    TooltipModule,
    HovergifDirective,
    Select,
    ToggleSwitch,
  ],
  templateUrl: './add-update-price-template.component.html',
  styleUrl: './add-update-price-template.component.scss',
})
export class AddUpdatePriceTemplateComponent implements OnDestroy {
  storeService = inject(StoreService);
  priceTemplateService = inject(PriceTemplateService);
  globalConfirmationService = inject(GlobalConfirmationService);
  baseCurrency =
    this.storeService.store$()?.panelSettings.defaultCurrency ||
    CurrencyEnum.TRY;
  priceTemplate: PriceTemplateInterface = new PriceTemplateClass();
  instance: DynamicDialogComponent | undefined;
  visible3 = false;
  rules: PriceTemplateRuleInterface | undefined;
  range: PriceTemplateRangeInterface = new PriceTemplateRangeClass();
  showError = false;
  rulesShowError = false;
  rulesForm$ = viewChild('rulesForm', { read: NgForm });
  addPriceTemplateForm$ = viewChild('addPriceTemplateForm', { read: NgForm });
  private destroy$ = new Subject<void>();
  subscriptionList: Subscription[] = [];

  priceSourceAll = priceSourceAll;

  constructor(
    public ref: DynamicDialogRef,
    private dialogService: DialogService
  ) {
    this.instance = this.dialogService.getInstance(this.ref);
    const instanceData = this.instance.data;

    this.priceSourceAll = priceSourceAll.filter(
      (f) => f.field !== 7 && f.field !== 8
    );

    if (!instanceData) {
      return;
    }

    if (this.instance.data.priceTemplate) {
      this.priceTemplate = JSON.parse(
        JSON.stringify(this.instance.data.priceTemplate ?? {})
      );
    } else {
      this.priceTemplate.MarketplaceIdentity =
        this.instance.data.marketplaceIdentity;
      this.priceTemplate.ProviderId = this.instance.data.providerId;
    }

    effect(() => {
      this.baseCurrency =
        this.storeService.store$()?.panelSettings.defaultCurrency ||
        CurrencyEnum.TRY;
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptionList.map((subscription) => {
      subscription.unsubscribe();
    });
  }

  newRulesModalOpen = () => {
    this.rulesForm$()?.form.reset();
    this.rulesShowError = false;
    this.showError = false;
    this.rules = new PriceTemplateRulesClass();
    this.visible3 = true;
  };
  updateRulesModalOpen = (rules: PriceTemplateRuleInterface) => {
    this.rules = { ...rules };
    this.rulesForm$()?.form.setValue({ Title: rules.Title });

    this.rulesShowError = false;
    this.showError = false;
    this.visible3 = true;
  };

  addRange = () => {
    if (!this.rules) return;

    const found = this.rules.Ranges.find((f) => {
      return (
        f.MinAmount === this.range.MinAmount &&
        this.range.MaxAmount === f.MaxAmount &&
        this.range.ChangeAmount === f.ChangeAmount
      );
    });

    if (!found) {
      this.rules.Ranges.push({ ...this.range });
      this.refreshSorting(this.rules.Ranges);

      this.showError = false;
    } else {
      this.showError = true;
    }
  };
  changeSwitch = () => {
    this.rulesShowError = false;
    this.showError = false;
    if (this.rules && !this.rules.IsPercent) {
      this.rules.DirectAmount = 0;
    }
    this.range = new PriceTemplateRangeClass();
  };

  deleteRangeItemDialog = (data: PriceTemplateRangeInterface) => {
    this.globalConfirmationService.deleteConfirmation(
      'Fiyat Koşulu Sil',
      `Fiyat koşulunu silmek istediğinize emin misiniz?`,
      () => {
        this.deleteRangeItem(data);
      }
    );
  };

  deleteRangeItem = (data: PriceTemplateRangeInterface) => {
    if (!this.rules) return;

    const found = this.rules.Ranges.filter((f) => {
      return !(
        data.MinAmount === f.MinAmount &&
        data.MaxAmount === f.MaxAmount &&
        data.ChangeAmount === f.ChangeAmount
      );
    });
    if (found) {
      this.rules.Ranges = [...found];
    }
  };

  addUpdatePriceRule = () => {
    if (!this.rules) return;

    if (
      this.rules?.Title?.length === 0 ||
      (this.rules.IsConditional && this.rules.Ranges.length === 0)
    ) {
      this.rulesShowError = true;
      return;
    }

    this.rulesShowError = false;

    if (this.rules.Sort === 0) {
      this.priceTemplate.Rules.push({ ...this.rules });
      this.refreshSorting(this.priceTemplate.Rules);
    } else {
      this.priceTemplate.Rules[this.rules.Sort - 1] = this.rules;
    }

    this.visible3 = false;
  };

  deleteRuleItemDialog = (data: PriceTemplateRuleInterface) => {
    this.globalConfirmationService.deleteConfirmation(
      'Fiyat Koşulu Sil',
      `Fiyat koşulunu silmek istediğinize emin misiniz?`,
      () => {
        this.deleteRule(data);
      }
    );
  };

  deleteRule = (data: PriceTemplateRuleInterface) => {
    if (!this.priceTemplate.Rules) return;
    const found = this.priceTemplate.Rules.filter((f) => {
      return !(f === data);
    });

    if (found) {
      this.priceTemplate.Rules = [...found];
      this.refreshSorting(this.priceTemplate.Rules);
    }
  };

  addUpdatePriceTemplate = () => {
    if (this.addPriceTemplateForm$()?.form?.invalid) {
      this.scrollToContent('add-price-template-form');
      this.showError = true;
      return;
    }
    /*
         if (this.priceTemplate.Rules.length === 0) {
         this.showError = true;
         this.scrollToContent('rule-table');
         return;
         }

         */
    this.showError = false;

    if (this.priceTemplate.Id.length === 0) {
      this.addPriceTemplate();
      return;
    }

    this.updatePriceTemplate();
  };

  addPriceTemplate = () => {
    const subscription = this.priceTemplateService
      .priceTemplateAdd(this.priceTemplate)
      .pipe(takeUntil(this.destroy$))
      .subscribe((f) => {
        this.subscriptionList.push(subscription);
        if (f && f.Status) {
          this.ref.close(f);
        }
      });
  };

  updatePriceTemplate = () => {
    const subscription = this.priceTemplateService
      .priceTemplateUpdate(this.priceTemplate)
      .pipe(takeUntil(this.destroy$))
      .subscribe((f) => {
        this.subscriptionList.push(subscription);
        if (f && f.Status) {
          this.ref.close(f);
        }
      });
  };

  scrollToContent(id: string) {
    setTimeout(() => {
      const element = document.querySelector(`#${id}`);

      element?.scrollIntoView({ behavior: 'smooth' });
    }, 30);
  }

  refreshSorting = (data: any[]) => {
    data.map((f: any, index) => {
      f.Sort = index + 1;
    });
  };

  protected readonly providerProductIncrementTypeOptions =
    providerProductIncrementTypeConst;
  protected readonly PercentageTypeEnum = PercentageTypeEnum;
  protected readonly ProviderProductIncrementTypeEnum =
    ProviderProductIncrementTypeEnum;
  protected readonly eventThrowingEnum = EventThrowingEnum;
}
